import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { InviteMemberDialog } from '@/components/InviteMemberDialog';
import { MemberRow, type Member } from '@/components/MemberRow';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

export function OrganizationMembers() {
  const { id } = useParams<{ id: string }>();
  const orgId = Number(id);
  const { user, idToken } = useCognitoAuth();
  const { organization } = useOrganization();

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutError, setMutError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const currentUserRole = members.find(
    (m) => m.email === user?.email
  )?.role;
  const canManage = currentUserRole === 'owner' || currentUserRole === 'admin';

  const fetchMembers = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Member[]>(`/api/organizations/${orgId}/members`, { errorCode: ErrorCode.FETCH_MEMBERS, token: idToken });
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [orgId, idToken]);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  const handleRemove = async (memberId: number) => {
    if (!idToken) return;
    setMutError(null);
    try {
      await api.delete(`/api/organizations/${orgId}/members/${memberId}`, { errorCode: ErrorCode.REMOVE_MEMBER, token: idToken });
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch (err) {
      setMutError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to remove member');
    }
  };

  const handleChangeRole = async (memberId: number, newRole: string) => {
    if (!idToken) return;
    setMutError(null);
    const prevRole = members.find((m) => m.id === memberId)?.role;
    try {
      await api.patch(`/api/organizations/${orgId}/members/${memberId}`, { role: newRole }, { errorCode: ErrorCode.CHANGE_MEMBER_ROLE, token: idToken });
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
      );
    } catch (err) {
      setMutError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to change role');
      // Revert to previous role
      if (prevRole !== undefined) {
        setMembers((prev) =>
          prev.map((m) => (m.id === memberId ? { ...m, role: prevRole } : m))
        );
      }
    }
  };

  const handleRevoke = async (invitationId: number) => {
    if (!idToken) return;
    setMutError(null);
    try {
      await api.delete(`/api/organizations/${orgId}/invitations/${invitationId}`, { errorCode: ErrorCode.REVOKE_INVITATION, token: idToken });
      setMembers((prev) => prev.filter((m) => m.invitation_id !== invitationId));
    } catch (err) {
      setMutError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to revoke invitation');
    }
  };

  const handleResend = async (invitationId: number) => {
    if (!idToken) return;
    setMutError(null);
    try {
      const updated = await api.post<Member>(`/api/organizations/${orgId}/invitations/${invitationId}/resend`, {}, { errorCode: ErrorCode.RESEND_INVITATION, token: idToken });
      setMembers((prev) =>
        prev.map((m) =>
          m.invitation_id === invitationId
            ? { ...m, invitation_expires_at: updated.invitation_expires_at, status: 'pending' }
            : m
        )
      );
    } catch (err) {
      setMutError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to resend invitation');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading members...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <i className="bi bi-exclamation-triangle text-3xl text-red-600 mb-3"></i>
        <p className="text-red-600 font-medium">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => void fetchMembers()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Members</h1>
          <p className="text-sm text-muted-foreground">
            {organization?.name ? `Manage ${organization.name} team members and invitations` : 'Manage team members and invitations'}
          </p>
        </div>
        {canManage && (
          <Button variant="default" size="sm" onClick={() => setInviteOpen(true)}>
            <i className="bi bi-person-plus mr-1"></i> Invite Member
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organization Members ({members.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {mutError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{mutError}</div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Name / Email</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Role</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-muted-foreground">
                      No members yet. Invite someone to get started.
                    </td>
                  </tr>
                ) : (
                  members.map((member) => (
                    <MemberRow
                      key={member.id}
                      member={{
                        ...member,
                        invitation_id: member.invitation_id ?? member.id,
                      }}
                      canManage={canManage}
                      currentUserId={user?.userId}
                      onRemove={handleRemove}
                      onChangeRole={handleChangeRole}
                      onRevoke={handleRevoke}
                      onResend={handleResend}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        organizationId={orgId}
        onSuccess={() => void fetchMembers()}
      />
    </div>
  );
}
